import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, ChevronDown, ChevronRight, History, LineChart, RefreshCw, Search } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { OrgUnitTree } from "@/components/org-unit/OrgUnitTree";
import { useLanguage } from "@/i18n/LanguageContext";
import type { OrgUnitHierarchy, OrgUnitOption } from "@/components/org-unit/OrgUnitTree";
import type { DashTab, FacilityTimeCounts, FacilityTimeResponse, FacilityTimeRow, FeedbackItem, NewIndicatorRow, MonthOption } from "./types";
import type { ApiResponse, AnomalyOptionsResponse } from "./types";
import { API_BASE_URL, DEFAULT_INDICATOR, DEFAULT_MONTH, DEFAULT_YEAR, FALLBACK_MONTHS, NEW_DATASET_DEFAULT_MONTH, NEW_DATASET_DEFAULT_YEAR, appendHierarchyParams, authHeaders, formatAnomalyType, formatPeriodLabel, formatRelativeTime, formatSeverity, getLast12MonthsWindow, toFiniteNumber } from "./utils";
import { AnomalySidebar } from "./components/AnomalySidebar";
import { TabBar } from "./components/TabBar";
import { ErrorMessage, RunButton, YearMonthField } from "./components/FormFields";
import { Th, Td, Severity, EmptyRow } from "./components/TablePrimitives";
import { FacilityTimeChart, FacilityTimeCountCards } from "./components/AnomalyCharts";
import { FeedbackAlertPanel } from "./components/FeedbackAlertPanel";
import { cacheScopeKey, createEmptyDashCache, loadActiveTab, loadDashCache, saveActiveTab, saveDashCache } from "./cache";
import type { DashCache } from "./cache";

interface DashPageProps {
  publicMode?: boolean;
}

const DEFAULT_HIERARCHY: OrgUnitHierarchy = {
  moh: "Federal Ministry Of Health",
  region: "",
  zone: "",
  woreda: "",
  phcu: "",
  level6: "",
  level7: "",
};

export const DashPage: React.FC<DashPageProps> = ({ publicMode = false }) => {
  const { t } = useLanguage();
  const cacheScope = cacheScopeKey(publicMode);

  // The last successful run is restored on mount so the charts render immediately
  // instead of waiting for the user to press Run again.
  const [bootCache] = useState<DashCache>(() => loadDashCache(cacheScope) ?? createEmptyDashCache());
  const cacheRef = useRef<DashCache>(bootCache);

  const [activeTab, setActiveTab] = useState<DashTab>(() => loadActiveTab(cacheScope) ?? "combined");
  const [lastRunAt, setLastRunAt] = useState<number | null>(bootCache.savedAt || null);

  const [sidebarYear, setSidebarYear] = useState(bootCache.time.year || DEFAULT_YEAR);
  const [sidebarMonth, setSidebarMonth] = useState(bootCache.time.month || DEFAULT_MONTH);
  const [sidebarHierarchy, setSidebarHierarchy] = useState<OrgUnitHierarchy>(() => {
    const cached = bootCache.time.hierarchy;
    const hasCachedLevel = Object.values(cached).some(Boolean);
    return hasCachedLevel ? { ...DEFAULT_HIERARCHY, ...cached, moh: cached.moh || DEFAULT_HIERARCHY.moh } : DEFAULT_HIERARCHY;
  });
  const [sidebarIndicator, setSidebarIndicator] = useState(bootCache.time.indicator || DEFAULT_INDICATOR);
  const [timeTableOpen, setTimeTableOpen] = useState(true);

  const [newTableOpen, setNewTableOpen] = useState(true);
  // --- Across Time Anomalies state ---
  const [timeRows, setTimeRows] = useState<FacilityTimeRow[]>(bootCache.time.rows);
  const [timeSeriesRows, setTimeSeriesRows] = useState<FacilityTimeRow[]>(bootCache.time.seriesRows);
  const [timeMethodRows, setTimeMethodRows] = useState<FacilityTimeRow[]>(bootCache.time.methodRows);
  const [timeCounts, setTimeCounts] = useState<FacilityTimeCounts>(bootCache.time.counts);
  const [timeHasRun, setTimeHasRun] = useState(bootCache.time.hasRun);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeError, setTimeError] = useState<string | null>(null);

  // Client-side filters for the time anomaly table
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([]);

  // --- Unexpected Datasets state ---
  const [newYear, setNewYear] = useState(bootCache.newIndicators.year || NEW_DATASET_DEFAULT_YEAR);
  const [newMonth, setNewMonth] = useState(bootCache.newIndicators.month || NEW_DATASET_DEFAULT_MONTH);
  const [baselineMonths, setBaselineMonths] = useState(bootCache.newIndicators.baselineMonths || 12);
  const [newRows, setNewRows] = useState<NewIndicatorRow[]>(bootCache.newIndicators.rows);
  const [newLoading, setNewLoading] = useState(false);
  const [newHasRun, setNewHasRun] = useState(bootCache.newIndicators.hasRun);
  const [newError, setNewError] = useState<string | null>(null);
  const [newOrgUnits, setNewOrgUnits] = useState<string[]>(bootCache.newIndicators.orgUnits);
  const [newOrgUnitSearch, setNewOrgUnitSearch] = useState("");
  const [newOrgUnitFilter, setNewOrgUnitFilter] = useState("");
  const [newDatasetFilter, setNewDatasetFilter] = useState("");
  const [newSeverityFilter, setNewSeverityFilter] = useState<"all" | "high" | "medium" | "low" | "new_reporting">("all");
  const [indicatorOptions, setIndicatorOptions] = useState<string[]>([]);
  const [orgUnitOptions, setOrgUnitOptions] = useState<OrgUnitOption[]>([]);
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [monthOptions, setMonthOptions] = useState<MonthOption[]>(FALLBACK_MONTHS);

  const persistCache = useCallback((patch: Partial<DashCache>) => {
    const next = { ...cacheRef.current, ...patch, savedAt: Date.now() } as DashCache;
    cacheRef.current = next;
    saveDashCache(cacheScope, next);
    setLastRunAt(next.savedAt);
  }, [cacheScope]);

  const unexpectedDatasetChartData = useMemo(() => {
    const totals = new Map<string, number>();
    newRows.forEach((row) => {
      const name = row.data_set_name || row.indicator || t("dash.new.unknownDataset");
      totals.set(name, (totals.get(name) || 0) + (toFiniteNumber(row.registration_rows) ?? toFiniteNumber(row.current_value) ?? 0));
    });
    return Array.from(totals.entries())
      .map(([dataset, rows]) => ({ dataset, rows }))
      .sort((a, b) => b.rows - a.rows);
  }, [newRows, t]);

  const matchingNewOrgUnits = useMemo(() => {
    const search = newOrgUnitSearch.trim().toLowerCase();
    if (!search) return [];
    return Array.from(new Set(
      orgUnitOptions
        .map((orgUnit) => orgUnit.name)
        .filter((name) => name.toLowerCase().includes(search) && !newOrgUnits.includes(name)),
    )).slice(0, 12);
  }, [newOrgUnitSearch, newOrgUnits, orgUnitOptions]);

  const unexpectedSummary = useMemo(() => {
    const orgUnits = new Set(newRows.map((row) => row.org_unit_id || row.facility).filter(Boolean));
    const datasets = new Set(newRows.map((row) => row.data_set_id || row.data_set_name || row.indicator).filter(Boolean));
    const rows = newRows.reduce((sum, row) => sum + (toFiniteNumber(row.registration_rows) ?? toFiniteNumber(row.current_value) ?? 0), 0);
    return { orgUnits: orgUnits.size, datasets: datasets.size, rows };
  }, [newRows]);

  const filteredNewRows = useMemo(() => {
    const orgNeedle = newOrgUnitFilter.trim().toLowerCase();
    const datasetNeedle = newDatasetFilter.trim().toLowerCase();
    return newRows.filter((row) => {
      const orgUnit = String(row.facility || row.org_unit_id || "").toLowerCase();
      const dataset = String(row.data_set_name || row.indicator || row.data_set_id || "").toLowerCase();
      const severity = String(row.severity || "").toLowerCase();
      const orgOk = !orgNeedle || orgUnit.includes(orgNeedle);
      const datasetOk = !datasetNeedle || dataset.includes(datasetNeedle);
      const severityOk = newSeverityFilter === "all" || severity === newSeverityFilter;
      return orgOk && datasetOk && severityOk;
    });
  }, [newDatasetFilter, newOrgUnitFilter, newRows, newSeverityFilter]);

  const apiAnomalyUrl = useMemo(
    () => publicMode ? `${API_BASE_URL}/public/anomalies` : `${API_BASE_URL}/anomalies`,
    [publicMode],
  );
  const runCombinedAnalyses = useCallback(async () => {
  const hierarchy = sidebarHierarchy;
  const indicator = sidebarIndicator;

  const { startYear, startMonth, endYear, endMonth } = getLast12MonthsWindow();

  const timeParams = new URLSearchParams({
    indicator,
    facility: "",
    medium_threshold: "1.5",
    high_threshold: "2.5",
    org_unit_levels: "3,4,5,6,7",
    start_year: startYear,
    start_month: startMonth,
    end_year: endYear,
    end_month: endMonth,
  });

  appendHierarchyParams(timeParams, hierarchy);

  let fetchedTimeRows: FacilityTimeRow[] = [];
  let fetchedTimeSeriesRows: FacilityTimeRow[] = [];
  let fetchedTimeMethodRows: FacilityTimeRow[] = [];
  let fetchedTimeCounts: FacilityTimeCounts = {};

  setTimeLoading(true);
  setTimeError(null);

  try {
    const res = await fetch(
      `${apiAnomalyUrl}/facility-time?${timeParams.toString()}`,
      { headers: authHeaders() }
    );

    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.detail) message = String(body.detail);
      } catch {}

      throw new Error(message);
    }

    const data = (await res.json()) as FacilityTimeResponse;

    fetchedTimeRows = Array.isArray(data.anomalies) ? data.anomalies : [];
    fetchedTimeSeriesRows = Array.isArray(data.series) ? data.series : [];
    fetchedTimeMethodRows = Array.isArray(data.method_rows) ? data.method_rows : [];
    fetchedTimeCounts = data.counts || {};

    setTimeRows(fetchedTimeRows);
    setTimeSeriesRows(fetchedTimeSeriesRows);
    setTimeMethodRows(fetchedTimeMethodRows);
    setTimeCounts(fetchedTimeCounts);
    setTimeHasRun(true);
    setSelectedScenarios([]);
    setSelectedSeverities([]);

    persistCache({
      time: {
        hasRun: true,
        indicator,
        hierarchy,
        year: sidebarYear,
        month: sidebarMonth,
        rows: fetchedTimeRows,
        seriesRows: fetchedTimeSeriesRows,
        methodRows: fetchedTimeMethodRows,
        counts: fetchedTimeCounts,
      },
    });
  } catch (e: any) {
    // Keep the last successful render on screen so a failed refresh never blanks
    // the graph the user is looking at.
    setTimeError(e?.message || t("dash.combined.requestFailed"));
  } finally {
    setTimeLoading(false);
  }
}, [apiAnomalyUrl, persistCache, sidebarHierarchy, sidebarIndicator, sidebarMonth, sidebarYear, t]);
  // const runCombinedAnalyses = useCallback(async () => {
  //   const hierarchy = sidebarHierarchy;
  //   const indicator = sidebarIndicator;

  //   const currentEY = parseInt(_ethiopianYear());
  //   const currentEM = parseInt(_ethiopianMonth());
  //   const fyLabel = currentEM >= 11 ? currentEY + 1 : currentEY;
  //   const startYear = String(fyLabel - 1);
  //   const startMonth = "11";
  //   const endYear = String(currentEY);
  //   const endMonth = _ethiopianMonth();
  //   const timeParams = new URLSearchParams({
  //     indicator, facility: "", medium_threshold: "1.5", high_threshold: "2.5",
  //     org_unit_levels: "3,4,5,6,7", start_year: startYear, start_month: startMonth,
  //     end_year: endYear, end_month: endMonth,
  //   });
  //   appendHierarchyParams(timeParams, hierarchy);

  //   let fetchedTimeRows: FacilityTimeRow[] = [];
  //   let fetchedTimeSeriesRows: FacilityTimeRow[] = [];
  //   let fetchedTimeMethodRows: FacilityTimeRow[] = [];
  //   let fetchedTimeCounts: FacilityTimeCounts = {};
  //   setTimeLoading(true);
  //   setTimeError(null);
  //   try {
  //     const res = await fetch(`${apiAnomalyUrl}/facility-time?${timeParams.toString()}`, { headers: authHeaders() });
  //     if (!res.ok) {
  //       let message = `Request failed (${res.status})`;
  //       try { const body = await res.json(); if (body?.detail) message = String(body.detail); } catch {}
  //       throw new Error(message);
  //     }
  //     const data = (await res.json()) as FacilityTimeResponse;
  //     fetchedTimeRows = Array.isArray(data.anomalies) ? data.anomalies : [];
  //     fetchedTimeSeriesRows = Array.isArray(data.series) ? data.series : [];
  //     fetchedTimeMethodRows = Array.isArray(data.method_rows) ? data.method_rows : [];
  //     fetchedTimeCounts = data.counts || {};
  //     setTimeRows(fetchedTimeRows);
  //     setTimeSeriesRows(fetchedTimeSeriesRows);
  //     setTimeMethodRows(fetchedTimeMethodRows);
  //     setTimeCounts(fetchedTimeCounts);
  //     setSelectedScenarios([]);
  //     setSelectedSeverities([]);
  //   } catch (e: any) {
  //     setTimeError(e?.message || "Request failed");
  //     setTimeRows([]); setTimeSeriesRows([]); setTimeMethodRows([]); setTimeCounts({});
  //   } finally {
  //     setTimeLoading(false);
  //   }

  // }, [apiAnomalyUrl, sidebarHierarchy, sidebarIndicator]);

  const runNewIndicatorsForAll = async () => {
    setNewLoading(true);
    setNewError(null);
    let fetchedNewRows: NewIndicatorRow[] = [];
    try {
      const params = new URLSearchParams({ year: newYear, month: newMonth, baseline_months: String(baselineMonths), limit: "500" });
      // The hierarchy filter matches the selected unit and every descendant
      // org unit, so a selected woreda/PHCU includes its facilities.
      appendHierarchyParams(params, sidebarHierarchy);
      newOrgUnits.forEach((orgUnit) => params.append("facility", orgUnit));
      const res = await fetch(`${apiAnomalyUrl}/new-indicators/all?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try { const body = await res.json(); if (body?.detail) message = String(body.detail); } catch {}
        throw new Error(message);
      }
      const data = (await res.json()) as ApiResponse<NewIndicatorRow> & { facility_count?: number };
      fetchedNewRows = Array.isArray(data.anomalies) ? data.anomalies : [];
      setNewRows(fetchedNewRows);
      setNewHasRun(true);
      setNewOrgUnitFilter(""); setNewDatasetFilter(""); setNewSeverityFilter("all");

      persistCache({
        newIndicators: {
          hasRun: true,
          year: newYear,
          month: newMonth,
          baselineMonths,
          orgUnits: newOrgUnits,
          rows: fetchedNewRows,
        },
      });
    } catch (e: any) {
      // Same as the time-series tab: a failed refresh keeps the last good chart.
      setNewError(e?.message || t("dash.combined.requestFailed"));
    } finally {
      setNewLoading(false);
    }
  };

  const toggleScenario = useCallback((scenario: string) => {
    setSelectedScenarios((prev) =>
      prev.includes(scenario) ? prev.filter((x) => x !== scenario) : [...prev, scenario],
    );
  }, []);

  const toggleSeverity = useCallback((severity: string) => {
    setSelectedSeverities((prev) =>
      prev.includes(severity) ? prev.filter((x) => x !== severity) : [...prev, severity],
    );
  }, []);

  const displayedTimeRows = useMemo(() => {
    return timeRows.filter((row) => String(row.anomaly_type).toLowerCase() !== "data_quality");
  }, [timeRows]);

  const uniqueScenarios = useMemo(() => {
    const set = new Set(displayedTimeRows.map((r) => r.anomaly_type).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [displayedTimeRows]);

  const uniqueSeverities = useMemo(() => {
    const set = new Set(displayedTimeRows.map((r) => r.severity).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [displayedTimeRows]);

  const filteredTimeAnomalyRows = useMemo(() => {
    return displayedTimeRows.filter((row) => {
      const scen = row.anomaly_type || "";
      const sev = row.severity || "";
      const scenOk = selectedScenarios.length === 0 || selectedScenarios.includes(scen);
      const sevOk = selectedSeverities.length === 0 || selectedSeverities.includes(sev);
      return scenOk && sevOk;
    });
  }, [displayedTimeRows, selectedScenarios, selectedSeverities]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const url = `${apiAnomalyUrl}/options`;
        const res = await fetch(url, { headers: authHeaders() });
        if (!res.ok) { console.warn("Options fetch failed", url, res.status, await res.text().catch(() => "")); return; }
        const data = (await res.json()) as AnomalyOptionsResponse;
        const years = Array.isArray(data.years) ? data.years : [];
        setIndicatorOptions(Array.isArray(data.indicators) ? data.indicators : []);
        setOrgUnitOptions(Array.isArray(data.org_units) ? data.org_units : []);
        setYearOptions(years);
        // Keep a restored period when the API still offers it, otherwise fall back.
        if (years.length > 0) {
          setSidebarYear((prev) => (years.includes(prev) ? prev : years[0]));
          setNewYear((prev) => (years.includes(prev) ? prev : years[0]));
        }
        // Always offer the full 12 Ethiopian months, preferring the API's own
        // labels for the ones it knows about.
        const monthsFromApi = Array.isArray(data.months) ? data.months : [];
        setMonthOptions(FALLBACK_MONTHS.map((fallback) => monthsFromApi.find((m) => m.value === fallback.value) ?? fallback));
      } catch {
        setIndicatorOptions([]); setOrgUnitOptions([]); setYearOptions([]); setMonthOptions(FALLBACK_MONTHS);
      }
    };
    fetchOptions();
  }, [apiAnomalyUrl]);

  const feedbackItems = useMemo<FeedbackItem[]>(() => {
    const timeItems = displayedTimeRows
      .filter((row) =>
        row.severity === "high" &&
        (row.anomaly_type === "trend" || row.anomaly_type === "rolling" || row.anomaly_type === "seasonal") &&
        row.value !== undefined &&
        row.value !== null,
      )
      .map((row, idx) => ({
        id: `time-${row.period || idx}`,
        type: t("dash.feedback.type.time"),
        severity: row.severity || "high",
        title: t("dash.feedback.title.text", {
          indicator: row.indicator || sidebarIndicator,
          path: [sidebarHierarchy.moh, sidebarHierarchy.region, sidebarHierarchy.zone, sidebarHierarchy.woreda, sidebarHierarchy.phcu, sidebarHierarchy.level6, sidebarHierarchy.level7].filter(Boolean).join(" › "),
        }),
        detail: t("dash.feedback.detail", {
          type: formatAnomalyType(row.anomaly_type, t),
          period: formatPeriodLabel(row.period),
          value: row.value ?? "-",
          expected: row.expected ?? "-",
          score: row.score ?? "-",
        }),
      }));

    return timeItems;
  }, [sidebarHierarchy, sidebarIndicator, displayedTimeRows, t]);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(true);

  useEffect(() => {
    saveActiveTab(cacheScope, activeTab);
  }, [activeTab, cacheScope]);

  useEffect(() => {
    if (activeTab === "new-indicators") {
      setSidebarOpen(true);
    }
  }, [activeTab]);

  return (
    <div className="flex min-h-screen flex-col" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(59,130,246,0.05) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(59,130,246,0.04) 0%, transparent 50%), #f8fafc' }}>
      <Navbar publicMode={publicMode} />
      <main className="flex-1 px-3 py-3 sm:px-4">
        <div className="mx-auto max-w-full">
          <div className="flex flex-col gap-3 xl:flex-row">
            <div className={`transition-all duration-200 xl:flex-shrink-0 xl:overflow-hidden ${
              activeTab === "combined"
                ? `w-full ${sidebarOpen ? 'xl:w-[240px]' : 'xl:w-0'}`
                : 'w-full xl:w-[260px] xl:overflow-visible'
            }`}>
              {activeTab === "combined" ? (
                <AnomalySidebar
                  publicMode={publicMode}
                  indicator={sidebarIndicator}
                  onIndicatorChange={setSidebarIndicator}
                  indicatorOptions={indicatorOptions}
                  year={sidebarYear}
                  month={sidebarMonth}
                  onYearChange={setSidebarYear}
                  onMonthChange={setSidebarMonth}
                  yearOptions={yearOptions}
                  monthOptions={monthOptions}
                  hierarchy={sidebarHierarchy}
                  onHierarchyChange={setSidebarHierarchy}
                  orgUnitOptions={orgUnitOptions}
                  loading={timeLoading}
                  onRun={runCombinedAnalyses}
                />
              ) : (
                <aside className="lg:sticky lg:top-1 lg:self-start">
                  <section className="card p-2.5">
                    <h3 className="text-[10px] font-medium text-slate-500">{t("dash.sidebar.orgUnit")}</h3>
                    <p className="mt-1 text-[9px] text-slate-500">{t("dash.sidebar.orgUnitHint")}</p>
                    <div className="mt-2 max-h-[65vh] overflow-auto">
                      <OrgUnitTree
                        orgUnits={orgUnitOptions}
                        selected={sidebarHierarchy}
                        onSelect={setSidebarHierarchy}
                        defaultExpandedDepth={1}
                      />
                    </div>
                  </section>
                </aside>
              )}
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`mt-1 hidden h-8 w-5 shrink-0 items-center justify-center rounded-3xl border border-primary-300/60 bg-primary-100 text-[9px] text-primary-600 shadow-soft transition-all hover:bg-primary-200 hover:text-primary-800 ${activeTab === "combined" ? 'xl:flex' : ''}`}
              title={sidebarOpen ? t("dash.action.collapseSidebar") : t("dash.action.expandSidebar")}
            >
              {sidebarOpen ? "◀" : "▶"}
            </button>

            {/* --- MAIN CONTENT --- */}
            <div className="min-w-0 flex-1 space-y-4">
              <TabBar activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === "combined" && (
          <div className="space-y-5">
            <section className="card overflow-hidden">
              <div className="border-l-2 border-primary-400 bg-gradient-to-r from-primary-50/40 to-transparent px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary-100">
                    <LineChart className="h-3.5 w-3.5 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-xs font-semibold text-slate-800">{t("dash.combined.title")}</h2>
                    <p className="text-[10px] text-primary-400 font-medium">{t("dash.combined.subtitle")}</p>
                  </div>
                  <LastRunBadge timestamp={lastRunAt} stale={Boolean(timeError) && timeSeriesRows.length > 0} />
                </div>
              </div>
              <div className="p-4">
              <ErrorMessage message={timeError} />
              <div className="rounded-2xl border border-primary-100/20 bg-primary-50/5 p-4 shadow-soft">
                {timeSeriesRows.length === 0 ? (
                  <div className="flex h-[256px] flex-col items-center justify-center gap-1 text-center text-xs text-slate-400">
                    {timeLoading ? (
                      t("common.loading")
                    ) : timeHasRun ? (
                      t("dash.combined.emptyNoData")
                    ) : (
                      <>
                        <span className="font-medium text-slate-500">{t("dash.combined.emptyNone")}</span>
                        <span>{t("dash.combined.emptyNoneDesc")}</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="relative space-y-4">
                    {timeLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/60">
                        <span className="flex items-center gap-2 rounded-full border border-primary-100 bg-white px-3 py-1 text-[10px] font-medium text-primary-600 shadow-soft">
                          <RefreshCw className="h-3 w-3 animate-spin" /> {t("dash.combined.refreshing")}
                        </span>
                      </div>
                    )}
                    <FacilityTimeCountCards counts={timeCounts} />
                    <FacilityTimeChart seriesRows={timeSeriesRows} methodRows={timeMethodRows} anomalyRows={timeRows} orgUnitOptions={orgUnitOptions} indicator={sidebarIndicator} anomHierarchy={sidebarHierarchy} anomYear={sidebarYear} anomMonth={sidebarMonth} />
                    <div className="text-[10px] text-slate-400 font-medium">
                      {[sidebarHierarchy.region, sidebarHierarchy.zone, sidebarHierarchy.woreda, sidebarHierarchy.phcu].filter(Boolean).length > 0 && (
                        <span>{[sidebarHierarchy.moh, sidebarHierarchy.region, sidebarHierarchy.zone, sidebarHierarchy.woreda, sidebarHierarchy.phcu, sidebarHierarchy.level6, sidebarHierarchy.level7].filter(Boolean).join(" › ")}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-6 border-t border-slate-100 pt-3 text-[10px]">
                      <div>
                        <span className="font-medium text-slate-500 mr-2">{t("dash.combined.scenario")}</span>
                        {uniqueScenarios.length === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          uniqueScenarios.map((s) => (
                            <label key={s} className="mr-3 inline-flex items-center gap-1.5">
                              <input type="checkbox" checked={selectedScenarios.includes(s)}
                                onChange={() => toggleScenario(s)}
                                className="rounded border-slate-300 text-primary-500 focus:ring-primary-500" />
                              <span className="text-slate-600">{formatAnomalyType(s, t)}</span>
                            </label>
                          ))
                        )}
                        {selectedScenarios.length > 0 && (
                          <button type="button" className="ml-1 text-[10px] text-primary-500 hover:text-primary-600 underline" onClick={() => setSelectedScenarios([])}>{t("dash.combined.clear")}</button>
                        )}
                      </div>
                      <div>
                        <span className="font-medium text-slate-500 mr-2">{t("dash.combined.severity")}</span>
                        {uniqueSeverities.length === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          uniqueSeverities.map((s) => (
                            <label key={s} className="mr-3 inline-flex items-center gap-1.5">
                              <input type="checkbox" checked={selectedSeverities.includes(s)}
                                onChange={() => toggleSeverity(s)}
                                className="rounded border-slate-300 text-primary-500 focus:ring-primary-500" />
                              <span className="text-slate-600">{formatSeverity(s, t)}</span>
                            </label>
                          ))
                        )}
                        {selectedSeverities.length > 0 && (
                          <button type="button" className="ml-1 text-[10px] text-primary-500 hover:text-primary-600 underline" onClick={() => setSelectedSeverities([])}>{t("dash.combined.clear")}</button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 cursor-pointer select-none pt-2 border-t border-primary-100/30" onClick={() => setTimeTableOpen(!timeTableOpen)}>
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100">
                        {timeTableOpen ? <ChevronDown className="h-3 w-3 text-primary-500" /> : <ChevronRight className="h-3 w-3 text-primary-500" />}
                      </div>
                      <span className="text-[10px] font-medium text-slate-600">{t("dash.combined.table")}</span>
                    </div>
                    {timeTableOpen && <FacilityTimeAnomalyTable rows={filteredTimeAnomalyRows} />}
                  </div>
                )}
              </div>
            </div>
            </section>


          </div>
          )}

          {activeTab === "new-indicators" && (
          <section className="card relative z-10 overflow-visible">
            <div className="border-l-2 border-primary-400 bg-gradient-to-r from-primary-50/40 to-transparent px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary-100">
                  <Activity className="h-3.5 w-3.5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xs font-semibold text-slate-800">{t("dash.new.title")}</h2>
                  <p className="text-[10px] text-primary-400 font-medium">{newYear}-{newMonth}</p>
                </div>
              {(sidebarHierarchy.moh || sidebarHierarchy.region) && (
                <span className="ml-2 text-[9px] text-slate-500">
                  {[sidebarHierarchy.moh, sidebarHierarchy.region, sidebarHierarchy.zone, sidebarHierarchy.woreda, sidebarHierarchy.phcu, sidebarHierarchy.level6, sidebarHierarchy.level7].filter(Boolean).join(" › ")}
                </span>
              )}
              <LastRunBadge timestamp={lastRunAt} stale={Boolean(newError) && newRows.length > 0} />
            </div>
            </div>
            <div className="p-4">
            <div className="mb-3">
            <div className="flex flex-wrap items-end gap-3">
              <YearMonthField label={t("dash.new.period")} year={newYear} month={newMonth} onYearChange={setNewYear}
                onMonthChange={setNewMonth} years={yearOptions} months={monthOptions} listId="new-year-options" />
              <div className="relative w-64 rounded-xl border border-primary-100 bg-primary-50/50 p-2 text-[10px] font-medium text-slate-600 shadow-sm">
                <span className="mb-1.5 flex items-center gap-1.5 font-semibold text-primary-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-100">
                    <Search className="h-3 w-3 text-primary-600" />
                  </span>
                  {t("dash.new.orgUnits")}
                  <span className="font-normal text-slate-400">{t("dash.new.optionalScope")}</span>
                </span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={newOrgUnitSearch}
                    onChange={(e) => setNewOrgUnitSearch(e.target.value)}
                    placeholder={t("dash.new.searchAddOrgUnits")}
                    className="input h-8 w-full border-primary-100 bg-white pl-7 text-[10px] font-medium text-slate-700 focus:border-primary-400"
                  />
                </div>
                {matchingNewOrgUnits.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
                    {matchingNewOrgUnits.map((orgUnit) => (
                      <button
                        key={orgUnit}
                        type="button"
                        onClick={() => {
                          setNewOrgUnits((selected) => [...selected, orgUnit]);
                          setNewOrgUnitSearch("");
                        }}
                        className="block w-full rounded px-2 py-1.5 text-left text-[10px] text-slate-700 hover:bg-primary-50"
                      >
                        {orgUnit}
                      </button>
                    ))}
                  </div>
                )}
                {newOrgUnits.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {newOrgUnits.map((orgUnit) => (
                      <button
                        key={orgUnit}
                        type="button"
                        onClick={() => setNewOrgUnits((selected) => selected.filter((item) => item !== orgUnit))}
                        className="rounded-full border border-primary-200 bg-white px-2 py-0.5 text-[9px] text-primary-700 shadow-sm hover:bg-primary-100"
                        title={t("dash.new.removeOrgUnit", { name: orgUnit })}
                      >
                        {orgUnit} ×
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <label className="hidden text-[10px] font-medium text-slate-600">
                <span className="mb-1 flex items-center gap-1">{t("dash.new.historyMonths")}</span>
                <input type="number" min={1} max={36} value={baselineMonths}
                  onChange={(e) => setBaselineMonths(Number(e.target.value || 12))}
                  className="input h-7 w-24" />
              </label>
              <div className="self-end sm:ml-auto flex items-center gap-2">
                <RunButton loading={newLoading} onClick={runNewIndicatorsForAll} />
              </div>
            </div>
            </div>
            <ErrorMessage message={newError} />
            {!newLoading && newRows.length === 0 && !newHasRun && (
              <div className="flex h-[256px] flex-col items-center justify-center gap-1 text-center text-xs text-slate-400">
                <span className="font-medium text-slate-500">{t("dash.new.emptyNone")}</span>
                <span>{t("dash.new.emptyNoneDesc", { year: newYear, month: newMonth })}</span>
              </div>
            )}
            {newRows.length > 0 && (
              <div className="mt-4 relative rounded-2xl border border-primary-100/20 bg-primary-50/5 p-4 shadow-soft">
                {newLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/60">
                    <span className="flex items-center gap-2 rounded-full border border-primary-100 bg-white px-3 py-1 text-[10px] font-medium text-primary-600 shadow-soft">
                      <RefreshCw className="h-3 w-3 animate-spin" /> {t("dash.combined.refreshing")}
                    </span>
                  </div>
                )}
                <div className="mb-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-cyan-200 bg-cyan-50/80 px-3 py-2">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-cyan-700">{t("dash.new.summaryOrgUnits")}</p>
                    <p className="text-base font-bold text-cyan-900">{unexpectedSummary.orgUnits}</p>
                  </div>
                  <div className="rounded-xl border border-violet-200 bg-violet-50/80 px-3 py-2">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-violet-700">{t("dash.new.summaryDatasets")}</p>
                    <p className="text-base font-bold text-violet-900">{unexpectedSummary.datasets}</p>
                  </div>
                  <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-amber-700">{t("dash.new.summaryRows")}</p>
                    <p className="text-base font-bold text-amber-900">{unexpectedSummary.rows}</p>
                  </div>
                </div>
                <div className="w-full overflow-x-auto">
                  <UnexpectedDatasetSvgChart data={unexpectedDatasetChartData} />
                </div>
              </div>
            )}
            {newHasRun && newRows.length === 0 && !newLoading && (
              <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-primary-100 bg-primary-50 shadow-soft">
                  <div className="h-3 w-3 rounded-full bg-primary-500 animate-ping" />
                  <div className="absolute h-3 w-3 rounded-full bg-primary-500" />
                </div>
                <h3 className="text-base font-semibold text-slate-600">{t("dash.new.emptyClear", { year: newYear, month: newMonth })}</h3>
                <p className="mt-1 text-xs text-slate-400">{t("dash.new.emptyClearDesc")}</p>
              </div>
            )}
            {newRows.length > 0 && (
              <div className="mt-3 animate-slide-up">
                <div className="rounded-2xl border border-slate-100 bg-slate-100/80 p-3 shadow-soft transition-all duration-300 hover:border-primary-100 hover:shadow-ambient">
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="text-[10px] font-medium text-slate-600 ">
                      <span className="mb-1 block">{t("dash.new.filterOrgUnit")}</span>
                       <input type="text" value={newOrgUnitFilter} onChange={(e) => setNewOrgUnitFilter(e.target.value)}
                        placeholder={t("dash.new.filterOrgUnitPlaceholder")} className="input h-7 w-52 text-[10px]" />
                    </label>
                    <label className="text-[10px] font-medium text-slate-600">
                      <span className="mb-1 block">{t("dash.new.filterDataset")}</span>
                      <input type="text" value={newDatasetFilter} onChange={(e) => setNewDatasetFilter(e.target.value)}
                        placeholder={t("dash.new.filterDatasetPlaceholder")} className="input h-7 w-52 text-[10px]" />
                    </label>
                    <button type="button" className="btn-secondary h-7 px-3 text-[10px] bg-blue-200/80 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                      onClick={() => { setNewOrgUnitFilter(""); setNewDatasetFilter(""); setNewSeverityFilter("all"); }}>{t("dash.new.clearFilters")}</button>
                    <div className="ml-auto text-[10px] text-slate-400">{t("dash.new.showing", { filtered: filteredNewRows.length, total: newRows.length })}</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 cursor-pointer select-none pt-1 transition-colors hover:text-primary-600" onClick={() => setNewTableOpen(!newTableOpen)}>
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 transition-transform duration-200 hover:scale-105">
                    {newTableOpen ? <ChevronDown className="h-3 w-3 text-primary-500 transition-transform duration-200" /> : <ChevronRight className="h-3 w-3 text-primary-500 transition-transform duration-200" />}
                  </div>
                  <span className="text-[10px] font-medium text-slate-600">{t("dash.new.dataTable")}</span>
                </div>
                {newTableOpen && (
                <div className="mt-2 max-h-96 overflow-auto rounded-2xl border border-slate-100 shadow-soft animate-fade-in">
                  <table className="min-w-[760px] w-full text-left text-xs">
                    <thead className="bg-primary-50/60 text-slate-600">
                      <tr><Th>{t("dash.new.th.orgUnit")}</Th><Th>{t("dash.new.th.dataset")}</Th><Th>{t("dash.new.th.firstPeriod")}</Th><Th>{t("dash.new.th.lastPeriod")}</Th><Th>{t("dash.new.th.timely")}</Th><Th>{t("dash.new.th.status")}</Th></tr>
                    </thead>
                    <tbody>
                      {filteredNewRows.length === 0 ? (
                        <EmptyRow colSpan={6} loading={false} />
                      ) : (
                        filteredNewRows.map((row, idx) => (
                          <tr key={`${row.org_unit_id || row.facility}-${row.data_set_id || row.indicator}-${idx}`} className="border-t border-slate-100">
                            <Td>{row.facility || "-"}</Td><Td>{row.data_set_name || row.indicator || "-"}</Td>
                            <Td>{row.first_reported_period || "-"}</Td><Td>{row.last_reported_period || "-"}</Td>
                            <Td>{row.timely_rows ?? "-"}</Td>
                            <td className="px-1.5 py-1">
                              <span className="rounded-full border border-cyan-200 bg-cyan-50 px-1.5 py-0.5 text-[9px] font-semibold text-cyan-700">
                                {t("dash.new.statusNew")}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}
           </div>
           </section>
          )}
            </div>

            <button
              type="button"
              onClick={() => setFeedbackOpen(!feedbackOpen)}
              className="mt-1 hidden h-8 w-5 shrink-0 items-center justify-center rounded-3xl border border-primary-300/60 bg-primary-100 text-[9px] text-primary-600 shadow-soft transition-all hover:bg-primary-200 hover:text-primary-800 xl:flex"
              title={feedbackOpen ? t("dash.action.collapseFeedback") : t("dash.action.expandFeedback")}
            >
              {feedbackOpen ? "▶" : "◀"}
            </button>
            <div className={`w-full transition-all duration-200 xl:flex-shrink-0 xl:overflow-hidden ${feedbackOpen ? 'xl:w-[220px]' : 'xl:w-0'}`}>
              <div className="w-full xl:w-[220px]">
                <FeedbackAlertPanel items={feedbackItems} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const LastRunBadge: React.FC<{ timestamp?: number | null; stale?: boolean }> = React.memo(({ timestamp, stale }) => {
  const { t } = useLanguage();
  if (!timestamp) return null;
  return (
    <span
      className={`ml-auto inline-flex shrink-0 items-center gap-1.5 self-center rounded-full border px-2 py-0.5 text-[9px] font-medium ${
        stale ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-white/70 text-slate-500"
      }`}
    >
      <History className="h-2.5 w-2.5" />
      {stale ? t("dash.badge.cached") : t("dash.badge.lastRun")} · {formatRelativeTime(timestamp, t)}
    </span>
  );
});
LastRunBadge.displayName = "LastRunBadge";

const FacilityTimeAnomalyTable: React.FC<{ rows: FacilityTimeRow[] }> = React.memo(({ rows }) => {
  const { t } = useLanguage();
  return (
  <div className="max-h-60 overflow-auto rounded-xl border border-slate-100 shadow-soft">
    <table className="w-full text-left text-xs">
      <thead className="bg-primary-50/60 text-slate-600">
        <tr><Th>{t("dash.table.scenario")}</Th><Th>{t("dash.table.period")}</Th><Th>{t("dash.table.value")}</Th><Th>{t("dash.table.expected")}</Th><Th>{t("dash.table.zscore")}</Th><Th>{t("dash.table.history")}</Th><Th>{t("dash.table.severity")}</Th></tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow colSpan={7} loading={false} />
        ) : (
          rows.map((row, idx) => (
            <tr key={`${row.period}-${row.anomaly_type}-${idx}`} className="border-t border-slate-100">
              <Td>{formatAnomalyType(row.anomaly_type, t)}</Td><Td>{formatPeriodLabel(row.period)}</Td>
              <Td>{row.value ?? "-"}</Td><Td>{row.expected ?? "-"}</Td><Td>{row.score ?? "-"}</Td>
              <Td>{row.history_points ?? "-"}</Td><Severity value={row.severity} />
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
  );
});
FacilityTimeAnomalyTable.displayName = "FacilityTimeAnomalyTable";

const UnexpectedDatasetSvgChart: React.FC<{ data: Array<{ dataset: string; rows: number }> }> = React.memo(({ data }) => {
  const { t } = useLanguage();
  const width = Math.max(520, data.length * 56);
  const height = 260;
  const margin = { top: 14, right: 18, bottom: 58, left: 44 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const maxRows = Math.max(...data.map((row) => toFiniteNumber(row.rows) ?? 0), 1);
  const barGap = 8;
  const barWidth = Math.max(12, Math.min(34, plotWidth / Math.max(data.length, 1) - barGap));
  const yForValue = (value: number) => margin.top + (1 - value / maxRows) * plotHeight;
  const yTicks = Array.from({ length: 4 }, (_, index) => (maxRows * index) / 3);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={t("dash.new.chart.aria")}>
      <rect width={width} height={height} fill="#ffffff" opacity="0.01" />
      {yTicks.map((tick) => {
        const y = yForValue(tick);
        return (
          <g key={tick}>
            <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="3 3" />
            <text x={margin.left - 8} y={y + 3} textAnchor="end" fontSize="9" fill="#475569">
              {Math.round(tick)}
            </text>
          </g>
        );
      })}
      {data.map((row, index) => {
        const value = toFiniteNumber(row.rows) ?? 0;
        const slot = plotWidth / Math.max(data.length, 1);
        const x = margin.left + index * slot + (slot - barWidth) / 2;
        const y = yForValue(value);
        const barHeight = Math.max(1, margin.top + plotHeight - y);
        const label = row.dataset.length > 18 ? `${row.dataset.slice(0, 15)}...` : row.dataset;
        return (
          <g key={`${row.dataset}-${index}`}>
            <title>{t("dash.new.chart.rows", { dataset: row.dataset, value })}</title>
            <rect x={x} y={y} width={barWidth} height={barHeight} rx="3" fill="#0e7490" />
            <text x={x + barWidth / 2} y={height - 28} textAnchor="end" fontSize="9" fill="#475569" transform={`rotate(-24 ${x + barWidth / 2} ${height - 28})`}>
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
});
UnexpectedDatasetSvgChart.displayName = "UnexpectedDatasetSvgChart";
